import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AnalyticsEvent, track } from '@bonusly/analytics';
import { useCurrentUser } from '@bonusly/who-am-i';

import { BizyCompleteEvent, useBizyChannel } from '../../../hooks/use-bizy-channel';
import { executePageAction, useActionSchemaRegistry } from '../../../lib/bizy-actions';
import { useBizyChatV2Mutation, useBizyStreamRecovery } from '../../../queries/bizy';
import type { BizyState } from '../../bizy';
import { clearBizySession, loadBizySession, saveBizySession } from '../lib/session-storage';
import { useBizyWidgetStore } from '../store/use-bizy-widget-store';
import { useBizyReportContext } from './use-bizy-report-context';

export type FeedbackType = 'positive' | 'negative' | undefined;

export interface ChatMessage {
	role: 'user' | 'assistant';
	content: string;
	/** ChatHistory ID from backend - only present for assistant messages */
	id?: string;
	/** User's feedback on this message */
	feedbackType?: FeedbackType;
	/** Stable key for React rendering - prevents remount when id changes (streaming) */
	key?: string;
	/** Tool names used by this message - populated from complete event */
	toolsUsed?: string[];
	/** When the message was created (client-side) */
	timestamp?: Date;
}

interface UseBizyGlobalChatOptions {
	extraContext?: Record<string, string>;
}

// Message roles
const ROLE_USER = 'user' as const;
const ROLE_ASSISTANT = 'assistant' as const;

// Default/fallback Bizy emotions
const EMOTION_CURIOUS: BizyState = 'curious';
const EMOTION_DISAPPOINTED: BizyState = 'disappointed';

// If no streaming event arrives within this window, assume the stream is dead
// and attempt recovery by polling the backend.
const STREAM_TIMEOUT_MS = 90_000;

/**
 * Generate a stable, unique message key using timestamp + counter.
 * The counter ensures uniqueness when multiple keys are created in the
 * same millisecond (e.g., when rehydrating a conversation from history).
 * Format: msg-{timestamp}-{counter} (e.g., "msg-1739471234567-0")
 */
let messageKeyCounter = 0;
function nextMessageKey(): string {
	return `msg-${Date.now()}-${messageKeyCounter++}`;
}

/**
 * Hook for managing Bizy streaming chat state in admin pages.
 *
 * Uses the POST /api/v2/bizy/chat/v2 endpoint which returns 202 immediately,
 * then receives the response via ActionCable (BizyChannel) as streaming tokens.
 *
 * This version is for general admin queries (no meeting context).
 *
 * Chat state persists in sessionStorage across widget close/open and page navigation
 * within the admin area, but clears when leaving admin or closing the browser tab.
 */
export function useBizyGlobalChat(options: UseBizyGlobalChatOptions = {}) {
	const { extraContext } = options;
	// Load session once to avoid triple sessionStorage reads/parses
	// and prevent inconsistent state if staleness boundary crossed between calls
	const sessionData = loadBizySession();

	const [messages, setMessages] = useState<ChatMessage[]>(sessionData?.messages || []);
	const [conversationId, setConversationId] = useState<string | undefined>(sessionData?.conversationId);
	const [bizyEmotion, setBizyEmotion] = useState<BizyState>(sessionData?.bizyEmotion || EMOTION_CURIOUS);
	const [isLoading, setIsLoading] = useState(false);
	const [activeToolCalls, setActiveToolCalls] = useState<string[]>([]);

	const currentUser = useCurrentUser();
	const { mutate } = useBizyChatV2Mutation();
	const recoverLastResponse = useBizyStreamRecovery();
	const reportContext = useBizyReportContext((state) => state.context);
	const resumeConversationId = useBizyWidgetStore((state) => state.resumeConversationId);
	const resumeMessages = useBizyWidgetStore((state) => state.resumeMessages);
	const clearResume = useBizyWidgetStore((state) => state.clearResume);
	const navigate = useNavigate();
	const { getSchema } = useActionSchemaRegistry();

	// Mirror isLoading state in a ref so streaming callbacks (which use [] deps
	// to avoid re-subscribing to ActionCable) can read the current value.
	const isLoadingRef = useRef(false);

	// --- Stream draining refs ---
	// When we reset the conversation while a stream is still active, late
	// onToken/onComplete/onError events from the previous request can arrive
	// after isLoadingRef is set back to true for the new request. These refs
	// allow us to silently discard those stale events.
	//
	// drainingRef: true while we're waiting for the old stream's terminal event
	// (complete/error) to arrive so we can swallow it.
	const drainingRef = useRef(false);
	// staleConversationIdRef: the conversation_id of the abandoned stream.
	// Provides a secondary guard on handleComplete — even if draining has
	// already cleared (e.g. due to event interleaving), we reject complete
	// events whose conversation_id matches the stale one.
	const staleConversationIdRef = useRef<string | undefined>();
	// Keep a ref mirror of conversationId so the reset effects can read the
	// current value without adding it to their dependency arrays.
	const conversationIdRef = useRef<string | undefined>();
	conversationIdRef.current = conversationId;
	const messagesRef = useRef(messages);
	messagesRef.current = messages;

	// Rehydrate conversation from history page when resume data is present
	useEffect(() => {
		if (resumeConversationId && resumeMessages) {
			cancelPendingFlush();

			// If an old stream is still active, drain it
			if (isLoadingRef.current) {
				drainingRef.current = true;
			}
			staleConversationIdRef.current = conversationIdRef.current;

			const hydrated: ChatMessage[] = resumeMessages.flatMap((msg) => [
				{ role: ROLE_USER, content: msg.userMessage, key: nextMessageKey(), timestamp: msg.createdAt },
				{
					role: ROLE_ASSISTANT,
					content: msg.bizyResponse,
					id: msg.id,
					feedbackType: msg.feedbackType ?? undefined,
					key: nextMessageKey(),
				},
			]);
			setMessages(hydrated);
			setConversationId(resumeConversationId);
			setBizyEmotion(EMOTION_CURIOUS);
			setIsLoading(false);
			isLoadingRef.current = false;
			setActiveToolCalls([]);
			streamingContentRef.current = '';
			clearResume();
		}
	}, [resumeConversationId, resumeMessages, clearResume]);

	// Track message send time for TTFT calculation
	const messageSentAtRef = useRef<number | null>(null);

	// Ref to accumulate streaming content for the current assistant message
	const streamingContentRef = useRef('');

	// rAF handle for batching token updates — caps renders at ~60fps
	const flushFrameRef = useRef(0);

	const cancelPendingFlush = useCallback(() => {
		if (flushFrameRef.current) {
			cancelAnimationFrame(flushFrameRef.current);
			flushFrameRef.current = 0;
		}
	}, []);

	// Clean up pending frame on unmount
	useEffect(() => cancelPendingFlush, [cancelPendingFlush]);

	// --- Stream timeout recovery ---
	const streamTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const recoverLastResponseRef = useRef(recoverLastResponse);
	recoverLastResponseRef.current = recoverLastResponse;

	const clearStreamTimeout = useCallback(() => {
		if (streamTimeoutRef.current) {
			clearTimeout(streamTimeoutRef.current);
			streamTimeoutRef.current = null;
		}
	}, []);

	const attemptStreamRecovery = useCallback(async () => {
		if (!isLoadingRef.current) return;

		const convId = conversationIdRef.current;
		const recovered = convId ? await recoverLastResponseRef.current(convId) : null;
		if (!isLoadingRef.current) return;

		const lastUserMessage = (() => {
			const msgs = messagesRef.current;
			for (let i = msgs.length - 1; i >= 0; i--) {
				if (msgs[i].role === ROLE_USER) return msgs[i].content;
			}
			return undefined;
		})();

		if (recovered && (!lastUserMessage || recovered.user_message === lastUserMessage)) {
			setMessages((prev) => {
				const lastMessage = prev[prev.length - 1];
				if (lastMessage?.role === ROLE_ASSISTANT) {
					return [...prev.slice(0, -1), { ...lastMessage, content: recovered.bizy_response, id: recovered.id }];
				}
				return [...prev, { role: ROLE_ASSISTANT, content: recovered.bizy_response, id: recovered.id, key: nextMessageKey() }];
			});
			setBizyEmotion((recovered.emotion as BizyState) || EMOTION_CURIOUS);
		} else {
			setMessages((prev) => {
				const lastMessage = prev[prev.length - 1];
				if (lastMessage?.role === ROLE_ASSISTANT && !lastMessage.id) {
					return [...prev.slice(0, -1), { ...lastMessage, content: 'Sorry, I lost my train of thought. Could you try again?' }];
				}
				return [...prev, { role: ROLE_ASSISTANT, content: 'Sorry, I lost my train of thought. Could you try again?', key: nextMessageKey() }];
			});
			setBizyEmotion(EMOTION_DISAPPOINTED);
		}

		setIsLoading(false);
		isLoadingRef.current = false;
		setActiveToolCalls([]);
		streamingContentRef.current = '';
		messageSentAtRef.current = null;
	}, []);

	const resetStreamTimeout = useCallback(() => {
		clearStreamTimeout();
		if (!isLoadingRef.current) return;
		streamTimeoutRef.current = setTimeout(() => attemptStreamRecovery(), STREAM_TIMEOUT_MS);
	}, [clearStreamTimeout, attemptStreamRecovery]);

	// BizyChannel event handlers
	const handleToken = useCallback(
		(content: string) => {
			// Drop tokens from a stale stream that we're draining
			if (drainingRef.current) return;
			// Drop tokens when no request is active
			if (!isLoadingRef.current) return;

			resetStreamTimeout();
			streamingContentRef.current += content;

			// Batch: accumulate tokens in ref, flush to state at most once per frame
			if (!flushFrameRef.current) {
				flushFrameRef.current = requestAnimationFrame(() => {
					flushFrameRef.current = 0;
					if (!isLoadingRef.current) return;

					const updatedContent = streamingContentRef.current;
					setMessages((prev) => {
						const lastMessage = prev[prev.length - 1];
						if (lastMessage?.role === ROLE_ASSISTANT && !lastMessage.id) {
							return [...prev.slice(0, -1), { ...lastMessage, content: updatedContent }];
						}
						return [...prev, { role: ROLE_ASSISTANT, content: updatedContent, key: nextMessageKey() }];
					});
				});
			}
		},
		[resetStreamTimeout]
	);

	const handleComplete = useCallback((data: BizyCompleteEvent['data']) => {
		clearStreamTimeout();
		// Swallow the terminal event from a stale stream we're draining
		if (drainingRef.current) {
			drainingRef.current = false;
			return;
		}
		// Drop completions when no request is active
		if (!isLoadingRef.current) return;
		// Secondary guard: reject completions whose conversation_id matches
		// the conversation we abandoned. This catches interleaving where the
		// old stream's complete arrives after draining already cleared.
		if (staleConversationIdRef.current && data.conversation_id === staleConversationIdRef.current) {
			staleConversationIdRef.current = undefined;
			return;
		}

		cancelPendingFlush();

		const ttftMs = messageSentAtRef.current && data.first_token_ms ? data.first_token_ms : undefined;

		// Track response received for analytics
		track(AnalyticsEvent.BizyAdmin.ResponseReceived, {
			responseTimeMs: data.response_time_ms,
			emotion: data.emotion || EMOTION_CURIOUS,
			success: true,
			toolsUsed: data.tools_used || [],
			ttftMs,
			path: window.location.pathname,
		});

		// Finalize the assistant message with the complete content, ID, and tool metadata.
		// Preserve the existing key so React doesn't remount the component.
		const toolsUsed = data.tools_used?.length ? data.tools_used : undefined;

		setMessages((prev) => {
			const lastMessage = prev[prev.length - 1];
			if (lastMessage?.role === ROLE_ASSISTANT) {
				return [
					...prev.slice(0, -1),
					{
						...lastMessage,
						content: data.content,
						id: data.message_id || undefined,
						toolsUsed,
					},
				];
			}
			// No in-progress message (shouldn't happen, but handle gracefully)
			return [
				...prev,
				{
					role: ROLE_ASSISTANT,
					content: data.content,
					id: data.message_id || undefined,
					key: nextMessageKey(),
					toolsUsed,
				},
			];
		});

		setConversationId(data.conversation_id);
		setBizyEmotion((data.emotion as BizyState) || EMOTION_CURIOUS);
		setIsLoading(false);
		isLoadingRef.current = false;
		setActiveToolCalls([]);
		streamingContentRef.current = '';
		messageSentAtRef.current = null;
	}, []);

	const handleError = useCallback((message: string) => {
		clearStreamTimeout();
		// Swallow the terminal event from a stale stream we're draining
		if (drainingRef.current) {
			drainingRef.current = false;
			return;
		}
		// Drop errors when no request is active
		if (!isLoadingRef.current) return;

		cancelPendingFlush();

		// Track error for analytics
		track(AnalyticsEvent.BizyAdmin.ResponseReceived, {
			success: false,
			errorType: 'streaming_error',
			path: window.location.pathname,
		});

		setMessages((prev) => {
			const lastMessage = prev[prev.length - 1];
			// If there's an in-progress assistant message (no id), replace it with the error
			if (lastMessage?.role === ROLE_ASSISTANT && !lastMessage.id) {
				return [...prev.slice(0, -1), { ...lastMessage, content: message }];
			}
			return [...prev, { role: ROLE_ASSISTANT, content: message, key: nextMessageKey() }];
		});

		setBizyEmotion(EMOTION_DISAPPOINTED);
		setIsLoading(false);
		isLoadingRef.current = false;
		setActiveToolCalls([]);
		streamingContentRef.current = '';
		messageSentAtRef.current = null;
	}, []);

	const handleToolCall = useCallback(
		(data: { tool_name: string }) => {
			if (!isLoadingRef.current) return;
			if (drainingRef.current) return;
			resetStreamTimeout();
			setActiveToolCalls((prev) => [...prev, data.tool_name]);
		},
		[resetStreamTimeout]
	);

	const handlePageAction = useCallback(
		async (data: any) => {
			// Drop page actions from a stale stream that we're draining
			if (drainingRef.current) return;
			// Drop page actions when no request is active
			if (!isLoadingRef.current) return;

			try {
				// Get schema using report_type from backend (sent back from context)
				const pageId = data.report_type;
				const schema = getSchema(pageId);

				await executePageAction(data, schema, navigate);
			} catch (error) {
				console.error('[Bizy] Failed to execute action:', error);
				console.error('[Bizy] Error stack:', error instanceof Error ? error.stack : 'No stack trace');
			}
		},
		[getSchema, navigate]
	);

	// Subscribe to BizyChannel
	useBizyChannel(currentUser?.id, {
		onToken: handleToken,
		onComplete: handleComplete,
		onError: handleError,
		onToolCall: handleToolCall,
		onPageAction: handlePageAction,
	});

	useEffect(() => clearStreamTimeout, [clearStreamTimeout]);

	// Persist state to sessionStorage whenever it changes
	useEffect(() => {
		saveBizySession({ messages, conversationId, bizyEmotion });
	}, [messages, conversationId, bizyEmotion]);

	// Recover truncated messages on mount.
	// If sessionStorage has an assistant message without an id, the complete
	// event was never received (killed worker, page refresh mid-stream, etc.).
	// Attempt to fetch the completed response from the backend.
	useEffect(() => {
		if (!sessionData?.conversationId || !sessionData?.messages?.length) return;
		const lastMsg = sessionData.messages[sessionData.messages.length - 1];
		if (lastMsg.role !== ROLE_ASSISTANT || lastMsg.id) return;

		const convId = sessionData.conversationId;
		const lastUserMsg = sessionData.messages.filter((m) => m.role === ROLE_USER).pop()?.content;

		recoverLastResponseRef.current(convId).then((recovered) => {
			if (isLoadingRef.current) return;

			if (!recovered || (lastUserMsg && recovered.user_message !== lastUserMsg)) {
				setMessages((prev) => {
					const last = prev[prev.length - 1];
					if (last?.role === ROLE_ASSISTANT && !last.id) {
						return prev.slice(0, -1);
					}
					return prev;
				});
				return;
			}
			setMessages((prev) => {
				const last = prev[prev.length - 1];
				if (last?.role === ROLE_ASSISTANT && !last.id) {
					return [...prev.slice(0, -1), { ...last, content: recovered.bizy_response, id: recovered.id }];
				}
				return prev;
			});
			setBizyEmotion((recovered.emotion as BizyState) || EMOTION_CURIOUS);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps -- runs once on mount with initial session data
	}, []);

	const sendMessage = useCallback(
		(content: string) => {
			cancelPendingFlush();
			const currentMessageCount = messages.length;

			// Track message sent for analytics
			track(AnalyticsEvent.BizyAdmin.MessageSent, {
				messageLength: content.length,
				conversationLength: currentMessageCount,
				isFirstMessage: currentMessageCount === 0,
				path: window.location.pathname,
			});

			// Clear drain state — we're starting a legitimate new request.
			// Any stale events arriving after this point will be for the old
			// stream, but since BizyChannel is per-user (not per-request) we
			// rely on the server processing requests sequentially so old
			// events arrive before new ones.
			drainingRef.current = false;
			staleConversationIdRef.current = undefined;

			// Add user message immediately for responsive UX
			setMessages((prev) => [...prev, { role: ROLE_USER, content, key: nextMessageKey(), timestamp: new Date() }]);
			setIsLoading(true);
			isLoadingRef.current = true;
			streamingContentRef.current = '';
			messageSentAtRef.current = Date.now();
			resetStreamTimeout();

			// Build context with surface info (no meeting partner for admin)
			const context: Record<string, string> = {
				page_path: window.location.pathname,
				...reportContext, // Include report context from store
				...(extraContext || {}),
			};

			mutate(
				{
					message: content,
					conversation_id: conversationId,
					context,
				},
				{
					onSuccess: (data) => {
						if (drainingRef.current) return;
						if (staleConversationIdRef.current && data.conversation_id === staleConversationIdRef.current) return;
						if (data.conversation_id) {
							setConversationId(data.conversation_id);
						}
					},
					onError: (error) => {
						clearStreamTimeout();
						track(AnalyticsEvent.BizyAdmin.ResponseReceived, {
							success: false,
							errorType: error instanceof Error ? error.name : 'unknown',
							path: window.location.pathname,
						});

						setMessages((prev) => [
							...prev,
							{
								role: ROLE_ASSISTANT,
								content: 'Sorry, I had trouble with that. Could you try again?',
								key: nextMessageKey(),
							},
						]);
						setBizyEmotion(EMOTION_DISAPPOINTED);
						setIsLoading(false);
						isLoadingRef.current = false;
						setActiveToolCalls([]);
						streamingContentRef.current = '';
						messageSentAtRef.current = null;
					},
				}
			);
		},
		[mutate, conversationId, messages.length, reportContext, extraContext]
	);

	const resetChat = useCallback(() => {
		cancelPendingFlush();
		clearStreamTimeout();

		// If an old stream is still active, drain it
		if (isLoadingRef.current) {
			drainingRef.current = true;
		}
		staleConversationIdRef.current = conversationIdRef.current;

		setMessages([]);
		setConversationId(undefined);
		setBizyEmotion(EMOTION_CURIOUS);
		setIsLoading(false);
		isLoadingRef.current = false;
		setActiveToolCalls([]);
		streamingContentRef.current = '';
		clearBizySession();
	}, [cancelPendingFlush, clearStreamTimeout]);

	const updateMessageFeedback = useCallback((messageId: string, feedbackType: FeedbackType) => {
		setMessages((prev) => prev.map((msg) => (msg.id === messageId ? { ...msg, feedbackType } : msg)));
	}, []);

	return {
		messages,
		conversationId,
		isLoading,
		bizyEmotion,
		activeToolCalls,
		sendMessage,
		resetChat,
		updateMessageFeedback,
	};
}
